import { Box, Typography, useTheme } from "@outschool/backpack";
import {
  Trans,
  useMachineTranslation,
  useTranslation
} from "@outschool/localization";
import { browseRootPath } from "@outschool/routes";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { MeetingFragment } from "@outschool/ui-components-classroom";
import { Loading } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";
import { useHistory, useLocation } from "react-router";

import * as Activity from "../../shared/Activity";
import * as Enrollment from "../../shared/Enrollment";
import * as Meeting from "../../shared/Meeting";
import * as Routes from "../../shared/Routes";
import { useTimeZone } from "../providers/TimeZoneProvider";
import ScheduleMeetingListing, {
  ScheduleClubListing
} from "./account/ScheduleMeetingListing";
import {
  ActiveEnrollmentForParentSchedule,
  ActivityForCalendarFragment,
  AgendaProps,
  CalendarEvent,
  MeetingListing
} from "./CalendarTypes";
import Link from "./Link";
import ScheduleCalendar, {
  LoadingContext,
  useCalendarQueryParams
} from "./ScheduleCalendar";

const DEFAULT_VIEW = "agenda";

type ParentScheduleCalendarProps = {
  enrollments: ActiveEnrollmentForParentSchedule[];
  loading: boolean;
  start: Date;
  ToolbarComponent: (arg0: any) => JSX.Element;
};

function ParentScheduleCalendar({
  enrollments,
  loading,
  start,
  ToolbarComponent
}: ParentScheduleCalendarProps) {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation("client\\components\\ParentScheduleCalendar");
  const { translate } = useMachineTranslation();
  const { view = DEFAULT_VIEW, date } = useCalendarQueryParams();
  const { timeZone: userTimeZone } = useTimeZone();
  const isMobile = Screen.useIsMobile();
  const { palette } = useTheme();

  const LEARNER_COLORS = [
    palette.info[800],
    palette.secondary[800],
    palette.success[800],
    palette.warning[800],
    palette.error[800]
  ];

  const views = isMobile
    ? { agenda: Agenda }
    : {
        agenda: Agenda,
        week: true,
        month: true
      };

  const [classEnrollments, clubEnrollments] = _.partition(
    enrollments,
    enrollment => !enrollment.section?.activity.isClub
  );

  const meetingListings: MeetingListing[] = Meeting.groupIntoMeetingListings({
    userTimeZone,
    enrollments: classEnrollments,
    cutoff: dayjs.tz(start, userTimeZone)
  });

  const meetingEvents = _.flatMap(meetingListings, meetingListing => {
    const { meeting, activity, enrollments } = meetingListing;
    if (!enrollments) {
      return [];
    }

    const start = dayjs.tz(meeting!.start_time, userTimeZone).toDate();
    const end = dayjs.tz(meeting!.end_time, userTimeZone).toDate();
    return enrollments.map(enrollment => ({
      id: meeting.uid || meeting.key,
      title: activity.titleTranslated,
      enrollment,
      section: enrollment.section,
      start,
      end,
      allDay: Activity.isFlexSchedule(activity),
      isClub: false,
      meeting
    }));
  });

  const clubEvents = clubEnrollments.map(enrollment => ({
    id: enrollment.uid,
    title: translate(enrollment.section?.activity?.title ?? "a Group"),
    enrollment,
    section: enrollment.section!,
    start: null,
    end: null,
    allDay: false,
    isClub: true
  }));

  // @ts-ignore TS(2531): Object is possibly 'null'.
  const uniqueClubEvents = _.uniqBy(clubEvents, e => e.section.uid);

  const events = _.uniqBy([...meetingEvents, ...uniqueClubEvents], event => {
    // Filter to unique events per learner, as there is some bad data
    // where learners have multiple enrollments in the same section.
    //
    // Determine uniqueness by event id and learner uid, so that parents with
    // multiple children in the same section see separate calendar events for
    // each learner.
    //
    // Some legacy enrollments may not have learners.
    const learnerUid = event.enrollment?.learner?.uid;

    return `${event.id}-${learnerUid}`;
  });

  // sort by created by to get consistent coloring over time and pages
  const learners = _.sortBy(
    _.uniqBy(
      events.map(event => event.enrollment?.learner),
      "uid"
    ),
    "created_at",
    "uid"
  );

  const eventPropGetter = (
    event: CalendarEvent
  ): React.HTMLAttributes<unknown> => {
    const learnerIndex = _.findIndex(
      learners,
      learner => learner?.uid === event.enrollment?.learner?.uid
    );
    const colorIndex = learnerIndex % LEARNER_COLORS.length;
    return {
      style: {
        backgroundColor: LEARNER_COLORS[colorIndex]
      }
    };
  };

  return (
    <ScheduleCalendar
      views={views}
      view={view}
      date={date}
      events={events}
      Toolbar={ToolbarComponent}
      Tooltip={({ allDay, start, title, enrollment }) => (
        <>
          {allDay
            ? t("{{studentName}} has {{classTitle}}", {
                studentName: Enrollment.studentNamesString([enrollment]),
                classTitle: title
              })
            : t("{{studentName}} has {{classTitle}} at {{scheduleAt}}", {
                studentName: Enrollment.studentNamesString([enrollment]),
                classTitle: title,
                scheduleAt: dayjs(start).format("ha")
              })}
        </>
      )}
      selectable={false}
      eventPropGetter={eventPropGetter}
      loading={loading}
      onNavigate={date => {
        if (dayjs().isSame(date, "day")) {
          const newQueryString = { ...location.query };
          delete newQueryString.date;
          history.replace({ query: newQueryString });
        } else {
          history.replace({
            query: {
              ...location.query,
              date: dayjs(date).format("YYYY-MM-DD")
            }
          });
        }
      }}
    />
  );
}

ParentScheduleCalendar.fragments = {
  enrollment: gql`
    fragment ParentScheduleCalendar_enrollment on Enrollment {
      uid
      confirmedAt
      withdrawnAt
      ongoingStartTime
      ongoingEndTime
      learner {
        uid
        name
        age
        avatar
        pronoun
        intro
        deleted_at
        created_at
        isVerified
      }
      section {
        uid
        start_time
        end_time
        meetingCount
        deleted_at
        published_at
        canceled_at
        filledSpaceCount
        activity {
          ...ActivityForCalendarFragment
          leader {
            uid
            name
            photo
            averageActivityStarRating
            reviewCount
          }
        }
        meetings(filter: Enrolled) {
          ...MeetingFragment
          sectionSequenceNumber
        }
        validEnrollmentCount
      }
      pauseWindow {
        uid
        startDate
        endDate
      }
    }
    ${ActivityForCalendarFragment}
    ${MeetingFragment}
  `
};

export default ParentScheduleCalendar;

function AgendaList({ events }: AgendaProps) {
  let monthLabel: $TSFixMe;
  let yearLabel = dayjs().format("YYYY");
  let weekId = dayjs().startOf("week").format("YYYYMMDD");
  const sortedEvents = _.uniqBy(events, "id").sort((a, b) => {
    return dayjs(a.start).isBefore(b.start) ? -1 : 1;
  });
  const agenda = sortedEvents.map(event => {
    const { start } = event;
    let monthLabelComp;
    const startDayjs = dayjs(start);
    const newMonthLabel = startDayjs.format("MMMM");
    if (monthLabel !== newMonthLabel) {
      monthLabel = newMonthLabel;
      monthLabelComp = (
        <Typography
          variant="h2"
          sx={{
            marginTop: "1em",
            marginBottom: "0.5em"
          }}
        >
          {monthLabel}
        </Typography>
      );
    }
    let yearLabelComp;
    const newYearLabel = startDayjs.format("YYYY");
    if (yearLabel !== newYearLabel) {
      yearLabel = newYearLabel;
      yearLabelComp = (
        <Typography
          variant="h2"
          sx={{
            marginTop: "1em"
          }}
        >
          {yearLabel}
        </Typography>
      );
    }
    const newWeekId = dayjs(startDayjs).startOf("week").format("YYYYMMDD");
    let weekComp;
    if (newWeekId !== weekId) {
      weekId = newWeekId;
      if (!monthLabelComp) {
        weekComp = (
          <hr style={{ marginTop: "medium", marginBottom: "medium" }} />
        );
      }
    }
    const meetingListing: MeetingListing = {
      meeting: event.meeting!,
      section: event.section,
      activity: event.section!.activity
    };
    return (
      <Box
        key={event.id}
        sx={{
          marginBottom: "1em"
        }}
      >
        {yearLabelComp}
        {monthLabelComp}
        {weekComp}
        <ScheduleMeetingListing meetingListing={meetingListing} />
      </Box>
    );
  });
  return <React.Fragment>{agenda}</React.Fragment>;
}

function ClubList({ events, t }: AgendaProps) {
  return (
    <>
      <Typography
        variant="h2"
        sx={{
          marginTop: "1em",
          marginBottom: "0.5em"
        }}
      >
        {/* @ts-ignore TS(2349): This expression is not callable. */}
        {t`Groups - Anytime`}
      </Typography>
      {events.map(e => (
        <ScheduleClubListing key={e.id} event={e} />
      ))}
    </>
  );
}

function Agenda({ events }: AgendaProps) {
  const [clubEvents, classEvents] = _.partition(events, e => e.isClub);
  const loading = React.useContext(LoadingContext);
  const { t } = useTranslation("client\\components\\ParentScheduleCalendar");
  return (
    <Box
      sx={{
        maxWidth: "1000px",
        position: "relative",
        width: "100%",
        marginLeft: "auto",
        marginRight: "auto",
        boxSizing: "border-box"
      }}
    >
      <Box
        sx={{
          marginBottom: "2em"
        }}
      >
        {/* eslint-disable react/no-unescaped-entities */}
        <Trans t={t}>
          Meetings for your learners’ enrolled classes.
          <br />
          For completed classes, see{" "}
          <Link to={Routes.transcriptsPath()}>your transcripts</Link>.
        </Trans>
        {/* eslint-enable react/no-unescaped-entities */}
      </Box>
      {loading ? (
        <Loading />
      ) : events.length === 0 ? (
        <Box>
          <Trans t={t}>
            Nothing scheduled yet.
            <br />
            <Link to={browseRootPath()}>Enroll in classes</Link> to see them
            here.
          </Trans>
        </Box>
      ) : (
        <>
          {clubEvents.length > 0 && <ClubList t={t} events={clubEvents} />}
          {classEvents.length > 0 && <AgendaList events={classEvents} />}
        </>
      )}
    </Box>
  );
}

Agenda.title = (date: $TSFixMe) => {
  const [start, end] = Agenda.range(date);
  return Time.fullDurationStringWithoutTimes(start, end);
};

Agenda.range = (date: $TSFixMe) => {
  return [date, dayjs(date).add(1, "week").endOf("day").toDate()];
};

Agenda.navigate = (date: $TSFixMe, action: $TSFixMe) => {
  switch (action) {
    case "PREV":
      return dayjs(date).subtract(1, "week").startOf("day").toDate();

    case "NEXT":
      return dayjs(date).add(1, "week").startOf("day").toDate();

    default:
      return date;
  }
};
